import { MouseEvent } from "react";
import { formatRelative } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  ChevronRight,
  X,
  MessageSquare,
  Target,
  User as UserIcon,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { InboxStageTag } from "./InboxStageTag";
import { PendingInboxItem } from "@forgepoint/shared";

export interface InboxItemRowProps {
  item: PendingInboxItem;
  agentMap: Map<string, { name: string }>;
  standardAgentName: string;
  onClick: () => void;
  onCancel?: (e: MouseEvent) => void;
  completed?: boolean;
  isAdmin?: boolean;
  currentUserId?: string;
  grouped?: boolean;
}

/**
 * A dense inbox item row showing task title, thread/opp context, and status.
 * @param item - The inbox item to render
 * @param agentMap - Map of agent workflow IDs to agent details
 * @param standardAgentName - Display name for the standard agent
 * @param onClick - Handler for clicking the row
 * @param onCancel - Handler for the cancel button (pending items only)
 * @param completed - Whether this is a completed item (dimmed styling)
 * @param isAdmin - Whether the current user is an admin (gates the cancel button)
 * @param currentUserId - The current user's id (gates the cancel button to assignees)
 * @param grouped - Whether the row sits under a group header (uses the shared left rail; per-row thread/opportunity/stage are only suppressed for single-opportunity agent SDK groups)
 */
export function InboxItemRow({
  item,
  agentMap,
  standardAgentName,
  onClick,
  onCancel,
  completed,
  isAdmin,
  currentUserId,
  grouped,
}: InboxItemRowProps) {
  const title = getItemTitle(item);
  const description = getItemDescription(item, agentMap, standardAgentName);
  // Only single-opportunity agent SDK groups suppress per-row context; workflow
  // (and legacy multi-opportunity) groups keep each row's opportunity/stage.
  const sharedContext = grouped && item.sourceType === "agent_sdk_review";
  const threadName =
    !grouped && item.sourceType === "agent_sdk_review" ? item.threadName : null;
  const assigneeNames = item.assignees.map((a) => a.name).join(", ");
  const assignedToMe = item.assignees.some((a) => a.userId === currentUserId);
  const canCancel = Boolean(onCancel) && (isAdmin || assignedToMe);
  const stateClasses = getRowStateClasses(completed, grouped);

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-all duration-75 group border-l-2 ${stateClasses}`}
      onClick={onClick}
    >
      {/* Status indicator */}
      {completed ? (
        <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 animate-in zoom-in-50 duration-300 ease-out" />
      ) : (
        <div className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
      )}

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-medium ${
              completed
                ? "line-through text-muted-foreground"
                : "group-hover:text-foreground"
            }`}
          >
            {title || <Skeleton className="h-4 w-32 inline-block" />}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-muted-foreground">{description}</span>
          {threadName && (
            <>
              <span className="text-xs text-muted-foreground/40">&middot;</span>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <MessageSquare className="h-3 w-3" />
                {threadName}
              </span>
            </>
          )}
          {!sharedContext && item.opportunityName && (
            <>
              <span className="text-xs text-muted-foreground/40">&middot;</span>
              <span className="inline-flex items-center gap-1 text-xs text-foreground/60">
                <Target className="h-3 w-3 shrink-0" />
                {item.opportunityName}
              </span>
            </>
          )}
          {assigneeNames && (
            <>
              <span className="text-xs text-muted-foreground/40">&middot;</span>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <UserIcon className="h-3 w-3 shrink-0" />
                {assigneeNames}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Stage column — right-aligned so stages line up across rows */}
      {!sharedContext && (
        <div className="hidden sm:flex w-32 shrink-0 justify-end">
          {item.stageName && <InboxStageTag name={item.stageName} />}
        </div>
      )}

      {/* Timestamp */}
      <span className="text-xs text-muted-foreground/70 shrink-0 tabular-nums">
        {formatRelative(item.updatedAt, new Date())}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {canCancel && onCancel && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={onCancel}
            aria-label="Cancel task"
            title="Cancel task"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all duration-75 group-hover:translate-x-0.5" />
      </div>
    </div>
  );
}

/**
 * Builds the row's state-dependent classes: completed rows dim, grouped rows
 * use a background hover only (the group's left rail is the shared vertical
 * line), and top-level rows get a red left accent on hover to draw attention.
 * @param completed - Whether the row is a completed item
 * @param grouped - Whether the row sits under a thread group header
 * @returns The Tailwind class string for the row's hover and border state
 */
function getRowStateClasses(completed?: boolean, grouped?: boolean): string {
  if (completed) {
    return "opacity-50 hover:opacity-70 hover:bg-muted/40 border-l-transparent";
  }
  if (grouped) {
    return "hover:bg-muted/60 border-l-transparent";
  }
  return "hover:bg-muted/50 border-l-transparent hover:border-l-red-500";
}

/**
 * Returns the primary display title for an inbox item.
 * Uses task subject when available, falls back to run/thread name.
 * @param item - The inbox item
 * @returns The title string
 */
export function getItemTitle(item: PendingInboxItem): string {
  if (item.subject) {
    return item.subject;
  }
  if (item.sourceType === "workflow_run_review") {
    return item.runName ?? "Untitled Task";
  }
  return item.threadName ?? "Untitled Task";
}

/**
 * Returns the secondary description for an inbox item.
 * @param item - The inbox item
 * @param agentMap - Map of agent workflow IDs to agent details
 * @param standardAgentName - Display name for the standard agent
 * @returns The description string
 */
export function getItemDescription(
  item: PendingInboxItem,
  agentMap: Map<string, { name: string }>,
  standardAgentName: string,
): string {
  if (item.sourceType === "workflow_run_review") {
    return agentMap.get(item.agentWorkflowId)?.name ?? item.agentWorkflowName;
  }
  return standardAgentName;
}
